'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, queryKeys } from '@/lib/api'
import type { AgentContactRecord } from '@/lib/agent-contact-utils'
import {
  contactMatchesIdentifier,
  extractContactIdentifiers,
  normalizeForComparison,
  sanitizeContactLookupValue,
} from '@/lib/agent-contact-utils'

type ContactMatchMap = Record<string, AgentContactRecord>

const buildSearchValues = (
  identifiers: readonly (string | null | undefined)[]
): string[] => {
  const unique = new Map<string, string>()

  identifiers.forEach(identifier => {
    const cleaned = sanitizeContactLookupValue(identifier)
    if (!cleaned) {
      return
    }

    const normalised = normalizeForComparison(cleaned)
    if (!normalised) {
      return
    }

    if (!unique.has(normalised)) {
      unique.set(normalised, cleaned)
    }
  })

  return Array.from(unique.values()).sort()
}

export const useAgentContactMatches = (
  identifiers: readonly (string | null | undefined)[]
) => {
  const searchValues = useMemo(
    () => buildSearchValues(identifiers),
    [identifiers]
  )

  return useQuery<ContactMatchMap>({
    queryKey: queryKeys.agentContacts.byIdentifiers(searchValues),
    enabled: searchValues.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const matches: ContactMatchMap = {}

      await Promise.all(
        searchValues.map(async searchValue => {
          const response = await api.sessions.searchContacts({
            search: searchValue,
            limit: 5,
            withPhoneOnly: false,
          })

          if (!response.success) {
            throw new Error(
              response.error || 'Failed to load contact information'
            )
          }

          const contacts = Array.isArray(response.data?.contacts)
            ? (response.data?.contacts as AgentContactRecord[])
            : []

          if (!contacts.length) {
            return
          }

          const normalizedSearch = normalizeForComparison(searchValue)
          const resolvedContact =
            contacts.find(contact =>
              contactMatchesIdentifier(contact, searchValue)
            ) ??
            (normalizedSearch
              ? contacts.find(contact =>
                  extractContactIdentifiers(contact).some(value => {
                    const normalizedCandidate = normalizeForComparison(value)
                    return normalizedCandidate === normalizedSearch
                  })
                )
              : undefined) ??
            contacts[0]

          if (!resolvedContact) {
            return
          }

          const keys = new Set<string>()
          if (normalizedSearch) {
            keys.add(normalizedSearch)
          }

          extractContactIdentifiers(resolvedContact).forEach(value => {
            const normalised = normalizeForComparison(value)
            if (normalised) {
              keys.add(normalised)
            }
          })

          keys.forEach(key => {
            if (!matches[key]) {
              matches[key] = resolvedContact
            }
          })
        })
      )

      return matches
    },
  })
}
